// components/layout/AdminLayout.tsx
/**
 * @mockup /design/mockups/05-desktop-admin-dashboard.png
 * Layout wrapper for admin pages with responsive sidebar navigation
 * Design System: Warm Sophistication - subtle gray background, warm accents
 */
import { type ReactNode, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building,
  Users,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  FileText,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getBuildings, type Building as BuildingType } from '../../lib/api';

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  end?: boolean;
  requiresSuper?: boolean;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === 'super_admin';
  const [buildingsExpanded, setBuildingsExpanded] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [buildings, setBuildings] = useState<BuildingType[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);

  // Fetch buildings from API
  useEffect(() => {
    async function fetchBuildings() {
      setBuildingsLoading(true);
      const { data, error } = await getBuildings();
      if (!error && data) {
        setBuildings(data.buildings);
      }
      setBuildingsLoading(false);
    }
    fetchBuildings();
  }, []);

  const navItems: NavItem[] = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/applications', icon: FileText, label: 'Inquiries' },
    { to: '/admin/users', icon: Users, label: 'Users', requiresSuper: true },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const filteredNavItems = navItems.filter(
    item => !item.requiresSuper || isSuper
  );

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen page-admin flex">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-surface border-b border-border z-40 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-text-secondary hover:text-text transition-colors"
        >
          <Menu size={24} />
        </button>
        <NavLink to="/" className="flex items-center hover:opacity-80 transition-opacity">
          <img src="/logo.png" alt="Beaux Properties" className="h-7" />
        </NavLink>
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-text/40 backdrop-blur-sm z-40 animate-fade-in"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 bottom-0 w-64 bg-surface border-r border-border flex flex-col z-50
        transform transition-transform duration-normal
        lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-14 lg:h-16 px-4 lg:px-5 flex items-center justify-between border-b border-border-subtle">
          <NavLink to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="Beaux Properties" className="h-8" />
          </NavLink>
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 -mr-2 text-text-muted hover:text-text transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {/* Dashboard */}
          <NavLink
            to="/admin"
            end
            onClick={closeSidebar}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-all duration-fast ${isActive
                ? 'bg-primary-subtle text-primary font-medium'
                : 'text-text-secondary hover:bg-bg-subtle hover:text-text'
              }`
            }
          >
            <LayoutDashboard size={20} />
            <span className="text-body-md">Dashboard</span>
          </NavLink>

          {/* Buildings Section */}
          <div className="mt-2">
            <button
              onClick={() => setBuildingsExpanded(!buildingsExpanded)}
              className="flex items-center justify-between w-full px-3 py-2.5 text-text-secondary hover:bg-bg-subtle hover:text-text rounded-md transition-all duration-fast"
            >
              <div className="flex items-center gap-3">
                <Building size={20} />
                <span className="text-body-md">Buildings</span>
              </div>
              {buildingsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {buildingsExpanded && (
              <div className="ml-6 mt-1 space-y-0.5">
                {buildingsLoading ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-text-muted text-body-sm">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Loading...</span>
                  </div>
                ) : buildings.length === 0 ? (
                  <div className="px-3 py-2 text-text-muted text-body-sm">
                    No buildings found
                  </div>
                ) : (
                  buildings.map((building) => (
                    <NavLink
                      key={building.id}
                      to={`/admin/buildings/${building.slug}`}
                      onClick={closeSidebar}
                      className={({ isActive }) =>
                        `block px-3 py-2 rounded-md text-body-sm transition-all duration-fast ${isActive
                          ? 'bg-primary-subtle text-primary font-medium'
                          : 'text-text-secondary hover:bg-bg-subtle hover:text-text'
                        }`
                      }
                    >
                      {building.name.split(' (')[0].replace(' Avenue', '').replace(' Street', ' St').replace(' Boulevard', ' Blvd').replace(' Drive', ' Dr')}
                    </NavLink>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Other Nav Items */}
          <div className="mt-4 pt-4 border-t border-border-subtle">
            {filteredNavItems.filter(item => item.to !== '/admin').map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-all duration-fast ${isActive
                    ? 'bg-primary-subtle text-primary font-medium'
                    : 'text-text-secondary hover:bg-bg-subtle hover:text-text'
                  }`
                }
              >
                <Icon size={20} />
                <span className="text-body-md">{label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-border-subtle">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-bg-subtle flex items-center justify-center border border-border">
              <span className="text-body-sm font-medium text-text-secondary">
                {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-body-sm font-medium text-text truncate">
                {user?.full_name || 'User'}
              </p>
              <p className="text-label-sm text-text-muted truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-body-sm text-text-secondary hover:text-text transition-colors duration-fast w-full px-2 py-1.5 rounded-md hover:bg-bg-subtle"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        {children}
      </main>
    </div>
  );
}
